import React, { useState } from 'react';
import {
  Modal,
  Upload,
  Button,
  Steps,
  Alert,
  Table,
  Typography,
  Space,
  Divider,
  Progress,
  Tag,
  message
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { deviceAPI } from '../utils/api';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;
const { Dragger } = Upload;

interface DeviceImportProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ImportResult {
  success: boolean;
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors: ImportError[];
  createdDevices: any[];
  updatedDevices: any[];
}

interface ImportError {
  row: number;
  deviceCode: string;
  categoryName: string;
  error: string;
}

const DeviceImport: React.FC<DeviceImportProps> = ({ visible, onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleDownloadTemplate = () => {
    try {
      deviceAPI.downloadTemplate();
      message.success('模板下载成功');
    } catch (error) {
      message.error('模板下载失败');
      console.error('下载模板失败:', error);
    }
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setCurrentStep(1);
    return false; // 阻止自动上传
  };

  const handleImport = async () => {
    if (!selectedFile) {
      message.error('请先选择文件');
      return;
    }

    setUploading(true);
    try {
      const result = await deviceAPI.importDevices(selectedFile);
      setImportResult(result.data);
      setCurrentStep(2);
      
      if (result.data.success) {
        message.success(`导入成功！共处理 ${result.data.totalRows} 行数据`);
        onSuccess();
      } else {
        message.warning(`导入完成，但有 ${result.data.errorCount} 行数据存在错误`);
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || '导入失败');
      console.error('导入失败:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setSelectedFile(null);
    setImportResult(null);
    onClose();
  };

  const errorColumns = [
    {
      title: '行号',
      dataIndex: 'row',
      key: 'row',
      width: 80,
    },
    {
      title: '设备代码',
      dataIndex: 'deviceCode',
      key: 'deviceCode',
      width: 120,
    },
    {
      title: '目录名称',
      dataIndex: 'categoryName',
      key: 'categoryName',
      width: 120,
    },
    {
      title: '错误信息',
      dataIndex: 'error',
      key: 'error',
      ellipsis: true,
    },
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <Alert
              message="批量导入说明"
              description={
                <div>
                  <Paragraph>
                    1. 点击下载模板，获取标准的XLSX格式文件
                  </Paragraph>
                  <Paragraph>
                    2. 在模板中填写设备信息：
                    <ul>
                      <li><strong>设备代码</strong>: 设备序列号（必填，唯一）</li>
                      <li><strong>目录名称</strong>: 目录名称（必填，必须是已存在的目录）</li>
                      <li><strong>用户标识</strong>: 用户标识（可选）</li>
                      <li><strong>设备描述</strong>: 设备描述（可选）</li>
                    </ul>
                  </Paragraph>
                  <Paragraph>
                    3. 如果设备已存在，将会移动到指定目录；如果不存在，将创建新设备
                  </Paragraph>
                  <Paragraph>
                    4. 如果目录不存在，该行数据将被跳过并报错
                  </Paragraph>
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownloadTemplate}
                size="large"
              >
                下载XLSX模板
              </Button>

              <Divider>选择要导入的文件</Divider>

              <Dragger
                accept=".xlsx"
                beforeUpload={handleFileSelect}
                showUploadList={false}
                style={{ padding: '20px' }}
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                </p>
                <p className="ant-upload-text">点击或拖拽XLSX文件到此区域</p>
                <p className="ant-upload-hint">
                  只支持XLSX格式文件，文件大小不超过5MB
                </p>
              </Dragger>
            </Space>
          </div>
        );

      case 1:
        return (
          <div>
            <Alert
              message="确认导入"
              description={`已选择文件: ${selectedFile?.name}`}
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Space>
              <Button onClick={() => setCurrentStep(0)}>
                重新选择文件
              </Button>
              <Button
                type="primary"
                loading={uploading}
                onClick={handleImport}
                icon={<UploadOutlined />}
              >
                {uploading ? '导入中...' : '开始导入'}
              </Button>
            </Space>
          </div>
        );

      case 2:
        return (
          <div>
            {importResult && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Alert
                  message={importResult.success ? "导入成功" : "导入完成（有错误）"}
                  type={importResult.success ? "success" : "warning"}
                  showIcon
                />

                <div style={{ padding: '16px', background: '#f5f5f5', borderRadius: '6px' }}>
                  <Title level={5}>导入统计</Title>
                  <Space wrap>
                    <Tag color="blue">总行数: {importResult.totalRows}</Tag>
                    <Tag color="green">成功: {importResult.successCount}</Tag>
                    <Tag color="red">失败: {importResult.errorCount}</Tag>
                  </Space>
                  
                  {importResult.successCount > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="success">
                        <CheckCircleOutlined /> 创建设备: {importResult.createdDevices.length} 个
                      </Text>
                      <br />
                      <Text type="success">
                        <CheckCircleOutlined /> 更新设备: {importResult.updatedDevices.length} 个
                      </Text>
                    </div>
                  )}
                </div>

                {importResult.errors.length > 0 && (
                  <div>
                    <Title level={5}>
                      <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} /> 错误详情
                    </Title>
                    <Table
                      dataSource={importResult.errors}
                      columns={errorColumns}
                      rowKey={(record) => `${record.row}-${record.deviceCode}`}
                      pagination={{ defaultPageSize: 100, pageSizeOptions: ['50', '100', '200', '500'], showSizeChanger: true }}
                      size="small"
                    />
                  </div>
                )}

                <Button type="primary" onClick={handleClose}>
                  完成
                </Button>
              </Space>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title="批量导入设备"
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        <Step title="下载模板" icon={<DownloadOutlined />} />
        <Step title="上传文件" icon={<UploadOutlined />} />
        <Step title="导入结果" icon={<InfoCircleOutlined />} />
      </Steps>

      {renderStepContent()}
    </Modal>
  );
};

export default DeviceImport;
